import {Dispatch, PropsWithChildren, useMemo} from "react";
import {
  createBundleReducer,
  initialCreateBundleState,
} from "../../reducers/create-bundle";
import {createContext} from "../../../../../context/create-context";
import {CreateBundleActions, CreateBundleState} from "../../reducers/create-bundle/types";
import {usePricingCurrencies} from "@/features/currencies";
import {useGetProfileContext} from "../../../../../context/profile/profile-context";
import {
  OFFERING_CREATE_DRAFT_KEYS,
  usePersistedReducer,
} from "../../../../../utils/offering-create-draft";

type CreateBundleContextState = {
  state: typeof initialCreateBundleState;
  dispatch: Dispatch<CreateBundleActions>;
  wasDraftRestored: boolean;
};

const [useContext, ContextProvider] = createContext<CreateBundleContextState>();

export const useCreateBundleContext = useContext;

export const CreateBundleProvider = ({children}: PropsWithChildren) => {
  const {state: profileState} = useGetProfileContext();
  const draftScope = useMemo(
    () => ({
      draftKey: OFFERING_CREATE_DRAFT_KEYS.bundle,
      email: profileState.profileData.email,
    }),
    [profileState.profileData.email]
  );

  const [state, dispatch, {wasDraftRestored}] = usePersistedReducer(
    createBundleReducer,
    draftScope,
    () => initialCreateBundleState
  );
  const pricingCurrencies = usePricingCurrencies();

  const newState: CreateBundleState = {
    currentCreateBundleStep: state.currentCreateBundleStep,
    formData: {
      ...state.formData,
      callPriceCurrency:
        state.formData.callPriceCurrency ?? pricingCurrencies.formCurrencies[0],
      callPriceType:
        state.formData.callPriceType ??
        (pricingCurrencies.connectedBankStatus.isBankConnected ? "paid" : "free"),
    },
    apiResponseData: state.apiResponseData,
  };

  return (
    <ContextProvider value={{state: newState, dispatch, wasDraftRestored}}>
      {children}
    </ContextProvider>
  );
};
